import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import EventIcon from '@mui/icons-material/Event';

import { ClubBadgeOverlay } from './ClubBadgeOverlay';
import CardMediaCarousel from './CardMediaCarousel';
import { sportMeta } from '../constants/sportMeta';
import { useFeedDate } from '../hooks/useFeedDate';
import { stringToColor } from '../utils/stringToColor';
import { FeedParticipant } from '../types/api';

export interface FeedCardMenuAction {
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
}

interface FeedCardProps {
    image?: string | null;
    /** When empty (e.g. the public races page), no club badge is shown. */
    clubName?: string;
    clubLogo?: string | null;
    sportType?: string | null;
    date?: string | null;
    title: string;
    subtitle?: React.ReactNode;
    stats?: string[];
    participants?: FeedParticipant[];
    participantLabel?: (count: number) => string;
    isParticipant?: boolean;
    primaryAction?: React.ReactNode;
    menuActions?: FeedCardMenuAction[];
    /** Internal route -- makes the image + body a link to a detail view. */
    href?: string;
    /**
     * Extra slides appended after the photo, turning the media area into a
     * swipable carousel (e.g. a start-point map, a weather forecast). Only
     * honoured on cards without an `href`.
     */
    extraMedia?: React.ReactNode[];
}

function initials(p: FeedParticipant): string {
    return `${p.firstname?.[0] ?? ''}${p.name?.[0] ?? ''}`.toUpperCase();
}

function hashString(value: string): number {
    let h = 0;
    for (let i = 0; i < value.length; i += 1) h = (h * 31 + value.charCodeAt(i)) | 0;
    return Math.abs(h);
}

function FeedCard(props: FeedCardProps) {
    const formatFeedDate = useFeedDate();
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [peopleAnchor, setPeopleAnchor] = useState<null | HTMLElement>(null);
    const [imageFailed, setImageFailed] = useState(false);

    const { primary, relative } = formatFeedDate(props.date);
    const sport = props.sportType ? sportMeta(props.sportType) : null;
    const participants = props.participants ?? [];
    const menuActions = props.menuActions ?? [];
    const hasFooter = participants.length > 0 || Boolean(props.primaryAction) || menuActions.length > 0;

    // No photo (or a broken one): a gradient tinted by the sport -- or the
    // club when there's no sport -- with a faded sport icon, so the card
    // still has an identity and every no-image card doesn't look identical.
    const showGeneratedCover = !props.image || imageFailed;
    const coverColor = sport?.color ?? stringToColor(props.clubName || props.title);
    const coverAngle = 110 + (hashString(props.title) % 80);

    const photoNode = showGeneratedCover ? (
        <Box
            aria-hidden
            sx={{
                width: '100%',
                height: '100%',
                display: 'grid',
                placeItems: 'center',
                overflow: 'hidden',
                background: `
                    radial-gradient(120% 80% at 18% 0%, rgba(255,255,255,0.22), transparent 55%),
                    radial-gradient(90% 90% at 100% 100%, rgba(0,0,0,0.3), transparent 55%),
                    linear-gradient(${coverAngle}deg, ${coverColor}, color-mix(in srgb, ${coverColor} 55%, #10140f))`,
                color: 'rgba(255,255,255,0.22)',
                '& svg': { fontSize: 76 },
            }}
        >
            {sport ? sport.icon : <EventIcon />}
        </Box>
    ) : (
        <CardMedia
            component="img"
            image={props.image as string}
            alt={props.title}
            onError={() => setImageFailed(true)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
    );

    const overlays = (
        <>
            {props.clubName && <ClubBadgeOverlay name={props.clubName} logo={props.clubLogo} />}
            {sport && !showGeneratedCover && (
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        zIndex: 2,
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        color: '#fff',
                        bgcolor: 'rgba(12, 18, 12, 0.6)',
                        backdropFilter: 'blur(4px)',
                        '& svg': { fontSize: 17 },
                    }}
                >
                    {sport.icon}
                </Box>
            )}
        </>
    );

    const extraMedia = props.extraMedia ?? [];
    const hasCarousel = !props.href && extraMedia.length > 0;

    const media = hasCarousel ? (
        <CardMediaCarousel slides={[photoNode, ...extraMedia]} overlay={overlays} ariaLabel={props.title} />
    ) : (
        <Box sx={{ position: 'relative', aspectRatio: '16 / 9', overflow: 'hidden' }}>
            {photoNode}
            {overlays}
        </Box>
    );

    const body = (
        <Box sx={{ p: '14px 16px 4px', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography
                component="p"
                sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.72rem',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: 'primary.main',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5ch',
                    m: 0,
                }}
            >
                <span>{primary}</span>
                {relative && <Box component="span" sx={{ color: 'text.disabled' }}>{relative}</Box>}
            </Typography>

            <Typography
                variant="h6"
                sx={{
                    fontSize: '1.15rem',
                    lineHeight: 1.25,
                    fontWeight: 600,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}
            >
                {props.title}
            </Typography>

            {props.subtitle && (
                <Typography variant="body2" color="text.secondary" noWrap>
                    {props.subtitle}
                </Typography>
            )}

            {props.stats && props.stats.length > 0 && (
                <Box
                    sx={{
                        mt: 0.5,
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '2px 8px',
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                        color: 'text.secondary',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {sport && (
                        <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: sport.color, mr: '2px' }} />
                    )}
                    {props.stats.map((stat, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && <Box component="span" sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'divider' }} />}
                            <span>{stat}</span>
                        </React.Fragment>
                    ))}
                </Box>
            )}
        </Box>
    );

    const footer = hasFooter && (
        <Box
            sx={{
                mt: 'auto',
                p: '12px 12px 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
            }}
        >
            <Box sx={{ display: 'flex', minWidth: 0 }}>
                {participants.length > 0 && (
                    <ButtonBase
                        onClick={(e) => setPeopleAnchor(e.currentTarget)}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, borderRadius: 1, p: 0.25, minWidth: 0 }}
                        aria-label={props.participantLabel ? props.participantLabel(participants.length) : String(participants.length)}
                    >
                        <AvatarGroup
                            max={4}
                            sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: '0.68rem', borderColor: 'background.paper' } }}
                        >
                            {participants.map((p) => (
                                <Avatar key={p.id} sx={{ bgcolor: stringToColor(p.name) }}>
                                    {initials(p)}
                                </Avatar>
                            ))}
                        </AvatarGroup>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {props.participantLabel ? props.participantLabel(participants.length) : participants.length}
                        </Typography>
                    </ButtonBase>
                )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 'none' }}>
                {props.primaryAction}
                {menuActions.length > 0 && (
                    <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="more">
                        <MoreHorizIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
        </Box>
    );

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                // reset the global `.App { text-align: center }` (CRA default)
                textAlign: 'left',
                transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
                ...(props.isParticipant && { borderColor: 'primary.main' }),
                '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
            }}
        >
            {props.href ? (
                <CardActionArea component={RouterLink} to={props.href} sx={{ display: 'block' }}>
                    {media}
                    {body}
                </CardActionArea>
            ) : (
                <>
                    {media}
                    {body}
                </>
            )}

            {footer}

            <Popover
                open={Boolean(peopleAnchor)}
                anchorEl={peopleAnchor}
                onClose={() => setPeopleAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <List dense sx={{ minWidth: 220, maxHeight: 280, overflowY: 'auto', py: 0.5 }}>
                    {participants.map((p) => (
                        <ListItem key={p.id}>
                            <ListItemAvatar sx={{ minWidth: 42 }}>
                                <Avatar sx={{ width: 30, height: 30, fontSize: '0.72rem', bgcolor: stringToColor(p.name) }}>
                                    {initials(p)}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText primary={`${p.firstname} ${p.name}`} />
                        </ListItem>
                    ))}
                </List>
            </Popover>

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                {menuActions.map((action, i) => (
                    <MenuItem
                        key={i}
                        onClick={() => {
                            setMenuAnchor(null);
                            action.onClick();
                        }}
                    >
                        <ListItemIcon>{action.icon}</ListItemIcon>
                        {action.label}
                    </MenuItem>
                ))}
            </Menu>
        </Card>
    );
}

export default FeedCard;
